import { useEffect, useRef, useState, useCallback } from "react";
import moment from "moment";
import { useAuth } from "../../../../../../authContext";
import { calendarSocket } from "../../../../../../../utils/Socket";
import { resumeAll, registerSocket } from "../../../../../../wsRecovery/wsResume";
import { startHeartbeat, stopHeartbeat } from "../../../../../../wsRecovery/heartbeat";
import {
  calendarLastSeen,
  getCalendarDiff,
  fetchCalendarPeriodEvents
} from "../api/calendarApi";
import { fetchEventByOccurrenceId as fetchOccurrenceEventFromApi } from "../../shared/services/eventDataService";
import { parseAuctionOccurrenceIdFromPath } from "../../shared/utils/eventLinks";

function formatEventsFromResponse(responseData, adjustedStart, adjustedEnd) {
  let formattedEvents = [];
  responseData.forEach((event) => {
    event.occurrences.forEach((occurrence) => {
      const occurrenceStart = moment(occurrence.startTime);
      const occurrenceEnd = moment(occurrence.endTime);
      if (
        occurrenceStart.isBetween(adjustedStart, adjustedEnd, undefined, "[]") &&
        occurrenceEnd.isBetween(adjustedStart, adjustedEnd, undefined, "[]")
      ) {
        const rawStatus = occurrence.participantStatus;
        const participantStatus =
          rawStatus === "signup" || rawStatus === "decline" ? rawStatus : "none";

        formattedEvents.push({
          id: event.id,
          occurrenceId: occurrence.id,
          title: event.eventName,
          start: occurrenceStart.toDate(),
          end: occurrenceEnd.toDate(),
          eventTypeId: event.eventTypeId,
          eventSubTypeId: event.eventSubTypeId,
          academyTrainingLevelId: event.academyTrainingLevelId,
          isTraining: event.isTraining,
          typeLabel: event.typeLabel || event.type || null,
          subTypeLabel: event.subTypeLabel || event.subType || null,
          trainingLevelLabel: event.trainingLevelLabel || event.trainingLevel || null,
          location: event.location,
          clearance: event.clearance,
          participantLimit: occurrence.participantLimit,
          participantLimitEnabled: occurrence.participantLimitEnabled === true,
          tentative: occurrence.tentative,
          isCancelled: occurrence.isCancelled,
          concludedAt: occurrence.concludedAt,
          participantStatus,
        });
      }
    });
  });

  formattedEvents = formattedEvents.reduce((acc, current) => {
    const x = acc.find((item) => item.id === current.id && item.start.getTime() === current.start.getTime());
    if (!x) return acc.concat([current]);
    return acc;
  }, []);

  return formattedEvents;
}

/**
 * Roster pattern: HTTP loads the grid (works without WS). Calendar WS is only for
 * join + missed/update + reconnect diff + resumeAll — same as useRosterSocket.
 */
export default function useCalendarEvents({
  userHasPermission,
  location,
  openCalendarEventModal,
  onOpenAuctionFromRoute,
  viewAsSubjectReady = true,
  viewAsUserId = null,
  isViewAsActive = false,
}) {
  const { isLoggedIn } = useAuth();
  const [events, setEvents] = useState([]);
  const [timezone, setTimezone] = useState("");
  const currentPeriodRef = useRef({ start: null, end: null });
  const eventsCountRef = useRef(0);

  const canReadCalendar =
    viewAsSubjectReady && userHasPermission("private.navbar.calendar", "read");
  const viewAsKey = isViewAsActive ? String(viewAsUserId ?? "") : "self";

  useEffect(() => {
    eventsCountRef.current = Array.isArray(events) ? events.length : 0;
  }, [events]);

  const refetchCurrentPeriod = useCallback(async () => {
    const { start, end } = currentPeriodRef.current;
    if (!start || !end) return;
    if (!canReadCalendar) {
      setEvents([]);
      return;
    }

    const adjustedStart = moment(start).subtract(1, "weeks").toISOString();
    const adjustedEnd = moment(end).add(1, "weeks").toISOString();

    try {
      const bundle = await fetchCalendarPeriodEvents(adjustedStart, adjustedEnd);
      if (!bundle?.ok) {
        console.error("[Calendar] period fetch failed:", bundle?.error);
        return;
      }
      const formattedEvents = formatEventsFromResponse(bundle.data, adjustedStart, adjustedEnd);
      setEvents(formattedEvents);
      const maxSeq = Number(bundle.maxWsLogSeq || 0);
      if (maxSeq > 0) {
        await calendarLastSeen(maxSeq).catch(() => {});
      }
    } catch (err) {
      console.error("[Calendar] period fetch error:", err);
    }
  }, [canReadCalendar, viewAsKey]);

  const fetchEventsForPeriod = useCallback(
    async (start, end) => {
      if (!canReadCalendar) {
        console.warn("Permission denied: private.navbar.calendar.read");
        setEvents([]);
        return;
      }
      currentPeriodRef.current = { start, end };
      await refetchCurrentPeriod();
    },
    [canReadCalendar, refetchCurrentPeriod]
  );

  const handleEventUpdated = useCallback(async () => {
    const startOfMonth = moment().startOf("month").toISOString();
    const endOfMonth = moment().endOf("month").toISOString();
    currentPeriodRef.current = { start: startOfMonth, end: endOfMonth };
    await refetchCurrentPeriod();
  }, [refetchCurrentPeriod]);

  useEffect(() => {
    const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(localTz);
  }, []);

  useEffect(() => {
    registerSocket("calendar", () => {
      if (!canReadCalendar || !isLoggedIn) return;
      if (!calendarSocket.connected) {
        calendarSocket.connect();
        startHeartbeat(calendarSocket);
      }
      refetchCurrentPeriod();
    });
  }, [canReadCalendar, isLoggedIn, refetchCurrentPeriod]);

  useEffect(() => {
    const startOfMonth = moment().startOf("month").toISOString();
    const endOfMonth = moment().endOf("month").toISOString();
    currentPeriodRef.current = { start: startOfMonth, end: endOfMonth };
    if (canReadCalendar && isLoggedIn) {
      refetchCurrentPeriod();
    } else {
      setEvents([]);
    }
  }, [canReadCalendar, isLoggedIn, refetchCurrentPeriod, viewAsKey]);

  // Match useRosterSocket: connect + reconnect + heartbeat lifecycle only
  useEffect(() => {
    if (!isLoggedIn || !canReadCalendar) return;

    if (!calendarSocket.connected) {
      calendarSocket.connect();
      startHeartbeat(calendarSocket);
    }

    const onConnect = () => {
      calendarSocket.emit("joinCalendar");
      resumeAll();
    };

    const onReconnect = async () => {
      calendarSocket.emit("joinCalendar");
      try {
        const diff = await getCalendarDiff();
        if (diff?.needRefresh) {
          await refetchCurrentPeriod();
        } else if (diff?.maxWsLogSeq) {
          await calendarLastSeen(Number(diff.maxWsLogSeq)).catch(() => {});
        }
      } catch (err) {
        console.error("[Calendar] reconnect diff failed:", err);
      }
    };

    calendarSocket.on("connect", onConnect);
    calendarSocket.on("reconnect", onReconnect);

    return () => {
      stopHeartbeat(calendarSocket);
      calendarSocket.off("connect", onConnect);
      calendarSocket.off("reconnect", onReconnect);
    };
  }, [isLoggedIn, canReadCalendar, refetchCurrentPeriod]);

  useEffect(() => {
    const handler = () => {
      if (!isLoggedIn || !canReadCalendar) return;
      if (!calendarSocket.connected) {
        calendarSocket.connect();
        startHeartbeat(calendarSocket);
      }
    };
    window.addEventListener("auth-ws-refreshed", handler);
    return () => window.removeEventListener("auth-ws-refreshed", handler);
  }, [isLoggedIn, canReadCalendar]);

  useEffect(() => {
    if (!isLoggedIn || !canReadCalendar) return;

    const resyncIfStale = async () => {
      try {
        const diff = await getCalendarDiff();
        if (diff?.needRefresh || eventsCountRef.current === 0) {
          await refetchCurrentPeriod();
        }
      } catch (err) {
        console.error("[Calendar] visibility/online diff failed:", err);
      }
    };

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      resyncIfStale();
    };

    const onOnline = () => {
      resyncIfStale();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
    };
  }, [isLoggedIn, canReadCalendar, refetchCurrentPeriod]);

  useEffect(() => {
    if (!isLoggedIn || !canReadCalendar) return;

    if (!calendarSocket.connected) {
      calendarSocket.connect();
      startHeartbeat(calendarSocket);
    }

    calendarSocket.emit("joinCalendar");

    const onMissed = async () => {
      try {
        await refetchCurrentPeriod();
      } catch (err) {
        console.error("[Calendar] calendarMissed refetch failed:", err);
      }
    };

    const onUpdate = async () => {
      try {
        await refetchCurrentPeriod();
      } catch (err) {
        console.error("[Calendar] calendarUpdate refetch failed:", err);
      }
    };

    calendarSocket.on("calendarMissed", onMissed);
    calendarSocket.on("calendarUpdate", onUpdate);

    return () => {
      calendarSocket.off("calendarMissed", onMissed);
      calendarSocket.off("calendarUpdate", onUpdate);
      calendarSocket.emit("leaveCalendar");
    };
  }, [isLoggedIn, canReadCalendar, refetchCurrentPeriod]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const occurrenceIdFromURL = searchParams.get("occurrenceId");
    const auctionOccurrenceId = parseAuctionOccurrenceIdFromPath(location.pathname);

    const openEventFromOccurrenceId = async (occurrenceId, { openAuction = false } = {}) => {
      if (!canReadCalendar) {
        console.warn("Permission denied: private.navbar.calendar.read");
        return;
      }
      try {
        const eventData = await fetchOccurrenceEventFromApi(occurrenceId);
        openCalendarEventModal({
          id: eventData.id,
          title: eventData.eventName,
          occurrenceId: occurrenceId,
          start: new Date(eventData.occurrences[0].startTime),
          end: new Date(eventData.occurrences[0].endTime)
        });
        if (openAuction && typeof onOpenAuctionFromRoute === "function") {
          onOpenAuctionFromRoute(occurrenceId);
        }
      } catch (error) {
        console.error("Error fetching event by occurrenceId:", error);
      }
    };

    if (auctionOccurrenceId) {
      openEventFromOccurrenceId(auctionOccurrenceId, { openAuction: true });
      return;
    }

    if (!occurrenceIdFromURL) return;
    openEventFromOccurrenceId(parseInt(occurrenceIdFromURL, 10), { openAuction: false });
  }, [
    location.search,
    location.pathname,
    canReadCalendar,
    openCalendarEventModal,
    onOpenAuctionFromRoute,
  ]);

  return { events, timezone, currentPeriodRef, fetchEventsForPeriod, handleEventUpdated };
}
